/** Llamadas HTTP del feature cierres mensuales. */
import { apiDownload, apiRequest } from "../../lib/api";

export type EstadoCierre =
  | "ABIERTO"
  | "EN_PREVALIDACION"
  | "PREVALIDADO"
  | "BLOQUEADO"
  | "EN_CALCULO"
  | "CALCULADO"
  | "EN_FACTURACION"
  | "FACTURADO"
  | "HOLD"
  | "CERRADO";

export interface CierreMensualRead {
  id: number;
  empresa_id: number;
  mes: number;
  anio: number;
  sector_id: number | null;
  observaciones: string | null;
  estado: EstadoCierre;
  motivo_bloqueo: string | null;
  fecha_hold: string | null;
  motivo_hold: string | null;
  total_contratos: number;
  lecturas_esperadas: number;
  lecturas_ingresadas: number;
  lecturas_estimadas: number;
  lecturas_con_alerta: number;
  lecturas_faltantes: number;
  total_m3: number;
  total_facturado: number;
  total_subsidios: number;
  total_multas: number;
  fecha_cierre: string | null;
  cerrado_por_id: number | null;
  hash_integridad: string | null;
  informe_siss_generado: boolean;
  informe_siss_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface PrevalidacionResponse {
  cierre_id: number;
  estado: EstadoCierre;
  total_contratos: number;
  lecturas_esperadas: number;
  lecturas_ingresadas: number;
  lecturas_estimadas: number;
  lecturas_con_alerta: number;
  lecturas_faltantes: number;
  bloqueantes: number;
  alertas: number;
}

export interface CierreValidacionDetalleRead {
  id: number;
  cierre_id: number;
  contrato_id: number | null;
  codigo: string;
  descripcion: string;
  bloquea: boolean;
  valor: string | null;
  resuelto: boolean;
  resuelto_por_id: number | null;
  resuelto_en: string | null;
  created_at: string;
  updated_at: string;
}

export interface AbrirCierrePayload {
  mes: number;
  anio: number;
  sector_id?: number | null;
  observaciones?: string | null;
}

export function listarCierresPorPeriodo(
  mes: number,
  anio: number,
): Promise<CierreMensualRead[]> {
  return apiRequest<CierreMensualRead[]>(
    `/api/v1/cierres?mes=${mes}&anio=${anio}`,
  );
}

export function obtenerCierre(id: number): Promise<CierreMensualRead> {
  return apiRequest<CierreMensualRead>(`/api/v1/cierres/${id}`);
}

export function listarValidaciones(
  cierreId: number,
  soloPendientes = false,
): Promise<CierreValidacionDetalleRead[]> {
  const qs = soloPendientes ? "?solo_pendientes=true" : "";
  return apiRequest<CierreValidacionDetalleRead[]>(
    `/api/v1/cierres/${cierreId}/validaciones${qs}`,
  );
}

export function abrirCierre(payload: AbrirCierrePayload): Promise<CierreMensualRead> {
  return apiRequest<CierreMensualRead>("/api/v1/cierres", {
    method: "POST",
    body: payload,
  });
}

export function prevalidarCierre(id: number): Promise<PrevalidacionResponse> {
  return apiRequest<PrevalidacionResponse>(`/api/v1/cierres/${id}/prevalidar`, {
    method: "POST",
  });
}

// ────────────────────────────────────────────────────────────────────────
// Tareas en segundo plano: estimar, calcular y facturar responden 202 con
// la tarea; la pantalla consulta su avance hasta que termina.
// ────────────────────────────────────────────────────────────────────────

export type TipoTareaCierre = "estimar_faltantes" | "calcular" | "facturar" | "emitir_sii";
export type EstadoTareaCierre = "en_curso" | "terminada" | "fallida";

export interface ResultadoTareaCierre {
  // estimar_faltantes
  creadas?: number;
  omitidos?: number[];
  m3_estimados?: number;
  // calcular / facturar
  boletas?: number;
  boletas_emitidas?: number;
  total_facturado?: number;
  total_m3?: number;
  // emitir_sii (DTE Fase C.2)
  emitidos?: number;
  conciliados?: number;
  errores?: number;
  inciertos?: number;
  pendientes?: number;
  interrumpida?: boolean;
}

export interface TareaCierre {
  id: number;
  cierre_id: number;
  tipo: TipoTareaCierre;
  estado: EstadoTareaCierre;
  /** Fase actual ("Emitiendo boletas"); avance/total son de esta etapa. */
  etapa: string | null;
  avance: number;
  total: number;
  parametros: { contrato_ids?: number[] } | null;
  resultado: ResultadoTareaCierre | null;
  error: string | null;
  creada_por_id: number | null;
  created_at: string;
  updated_at: string;
  terminada_at: string | null;
}

/** Última tarea del cierre (null si nunca tuvo una). */
export function obtenerTareaActual(cierreId: number): Promise<TareaCierre | null> {
  return apiRequest<TareaCierre | null>(`/api/v1/cierres/${cierreId}/tareas/actual`);
}

/** Lanza el cálculo en segundo plano. */
export function calcularCierre(id: number): Promise<TareaCierre> {
  return apiRequest<TareaCierre>(`/api/v1/cierres/${id}/calcular`, {
    method: "POST",
  });
}

/** Lanza la emisión en segundo plano. */
export function facturarCierre(id: number): Promise<TareaCierre> {
  return apiRequest<TareaCierre>(`/api/v1/cierres/${id}/facturar`, {
    method: "POST",
  });
}

/** Emite al SII los borradores del cierre y concilia los inciertos (DTE C.2). */
export function emitirSii(id: number): Promise<TareaCierre> {
  return apiRequest<TareaCierre>(`/api/v1/cierres/${id}/emitir-sii`, {
    method: "POST",
  });
}

export function cerrarCierre(id: number): Promise<CierreMensualRead> {
  return apiRequest<CierreMensualRead>(`/api/v1/cierres/${id}/cerrar`, {
    method: "POST",
  });
}

/** Envía el cierre a revisión de un supervisor (FACTURADO → HOLD). */
export function enviarARevision(
  id: number,
  motivo: string,
): Promise<CierreMensualRead> {
  return apiRequest<CierreMensualRead>(`/api/v1/cierres/${id}/hold`, {
    method: "POST",
    body: { motivo },
  });
}

/** Termina la revisión del supervisor (HOLD → FACTURADO). */
export function liberarRevision(id: number): Promise<CierreMensualRead> {
  return apiRequest<CierreMensualRead>(`/api/v1/cierres/${id}/liberar-hold`, {
    method: "POST",
  });
}

/**
 * Descarga el PDF con todas las boletas emitidas del cierre (una por página,
 * en orden de reparto: ruta → posición). Con `rutaId` sólo esa ruta — una
 * pila por operador. Sólo con el cierre FACTURADO / HOLD / CERRADO.
 */
export function descargarBoletasPdf(
  cierreId: number,
  rutaId?: number,
): Promise<void> {
  const qs = rutaId != null ? `?ruta_id=${rutaId}` : "";
  return apiDownload(
    `/api/v1/cierres/${cierreId}/boletas.pdf${qs}`,
    `boletas_cierre_${cierreId}.pdf`,
  );
}

// ────────────────────────────────────────────────────────────────────────
// Proforma — vista previa de lo que se va a facturar
// ────────────────────────────────────────────────────────────────────────

export type AlertaProformaCodigo =
  | "CONSUMO_ALTO"
  | "CONSUMO_CERO"
  | "SIN_HISTORICO"
  | "MONTO_ALTO"
  | "ESTIMADA";

export type AlertaSeveridad = "info" | "warn" | "danger";

export interface AlertaProforma {
  codigo: AlertaProformaCodigo;
  descripcion: string;
  severidad: AlertaSeveridad;
}

export interface LecturaProforma {
  lectura_id: number;
  contrato_id: number;
  socio_id: number;
  socio_nombre: string;
  socio_rut: string;
  socio_direccion: string | null;
  arranque_codigo: string | null;
  arranque_direccion: string;
  sector_id: number | null;
  sector_nombre: string | null;
  medidor_id: number;
  medidor_numero_serie: string | null;
  medidor_diametro: string | null;
  lectura_anterior: number;
  lectura_actual: number;
  consumo_m3: number;
  tipo_lectura: string;
  fecha_lectura: string | null;
  monto_cargo_fijo: number;
  monto_consumo: number;
  monto_alcantarillado: number;
  monto_subsidio: number;
  monto_multas: number;
  monto_total: number;
  detalle_tramos: string | null;
  /** Socio → boleta exenta; cliente → boleta afecta con IVA (como el PDF). */
  es_socio: boolean;
  alertas: AlertaProforma[];
}

export interface EmpresaInfoProforma {
  id: number;
  nombre: string;
  rut: string;
  direccion: string | null;
  telefono: string | null;
  email: string;
  logo_url: string | null;
  mensaje_boleta: string | null;
  texto_resolucion_sii: string | null;
}

export interface ProformaSumarios {
  boletas_a_emitir: number;
  total_m3: number;
  total_cargo_fijo: number;
  total_consumo: number;
  total_alcantarillado: number;
  total_subsidios: number;
  total_multas: number;
  total_facturado: number;
}

export interface ProformaComparativa {
  mes_anterior_existe: boolean;
  mes_anterior_total_facturado: number;
  mes_anterior_total_m3: number;
  mes_anterior_boletas: number;
  delta_pct_facturado: number | null;
  delta_pct_m3: number | null;
}

export interface ProformaResponse {
  cierre_id: number;
  estado: EstadoCierre;
  mes: number;
  anio: number;
  sector_id: number | null;
  empresa: EmpresaInfoProforma;
  sumarios: ProformaSumarios;
  comparativa: ProformaComparativa;
  lecturas: LecturaProforma[];
  lecturas_con_alertas: number;
}

export function obtenerProforma(cierreId: number): Promise<ProformaResponse> {
  return apiRequest<ProformaResponse>(`/api/v1/cierres/${cierreId}/proforma`);
}

// ────────────────────────────────────────────────────────────────────────
// Faltantes y término medio (Manual SISS cap. 4)
// ────────────────────────────────────────────────────────────────────────

export type MotivoNoEstimable = "sin_medidor" | "sin_historial";

export interface FaltanteCierre {
  contrato_id: number;
  socio_nombre: string;
  socio_rut: string | null;
  arranque_codigo: string | null;
  arranque_direccion: string | null;
  sector_id: number | null;
  estimable: boolean;
  motivo_no_estimable: MotivoNoEstimable | null;
  lectura_anterior: number;
  termino_medio_m3: number;
  lectura_estimada: number;
  meses_base: number;
  base_debil: boolean;
  consumos_base: number[];
}

/** Vista previa: qué casas quedaron sin lectura y qué promedio les toca. */
export function listarFaltantes(cierreId: number): Promise<FaltanteCierre[]> {
  return apiRequest<FaltanteCierre[]>(`/api/v1/cierres/${cierreId}/faltantes`);
}

/** Lanza la creación de las lecturas Estimadas en segundo plano. Sin
 * `contratoIds` toma todas las estimables. */
export function estimarFaltantes(
  cierreId: number,
  contratoIds?: number[],
): Promise<TareaCierre> {
  return apiRequest<TareaCierre>(`/api/v1/cierres/${cierreId}/estimar-faltantes`, {
    method: "POST",
    body: { contrato_ids: contratoIds ?? null },
  });
}

/** Vuelve a lanzar una tarea fallida con los mismos parámetros. */
export function reintentarTarea(tarea: TareaCierre): Promise<TareaCierre> {
  switch (tarea.tipo) {
    case "estimar_faltantes":
      return estimarFaltantes(tarea.cierre_id, tarea.parametros?.contrato_ids);
    case "calcular":
      return calcularCierre(tarea.cierre_id);
    case "facturar":
      return facturarCierre(tarea.cierre_id);
    case "emitir_sii":
      return emitirSii(tarea.cierre_id);
  }
}
